import type { RuleSet } from '../../rules/schema';
import { invalidateDerived } from '../derivedRegistry';
import { fromMilli } from '../fixed';
import { buildingInvestment, upgradeFraction } from '../levels';
import { matchDuration, matchHost } from '../settings';
import { playerStock } from '../stock';
import { isCity } from '../territory';
import { queuedUnits } from '../training';
import type { PlayerId, SimState, SystemFn } from '../types';

const ELAPSED_EPSILON = 1e-6;

/** Marks `playerId` out of the match and removes everything they own. */
function eliminatePlayer(state: SimState, playerId: PlayerId): void {
  for (const [id, unit] of Object.entries(state.units)) {
    if (unit.owner === playerId) delete state.units[id];
  }
  for (const [id, building] of Object.entries(state.buildings)) {
    if (building.owner === playerId) delete state.buildings[id];
  }
  state.players[playerId].eliminated = true;
  invalidateDerived(state);
}

const TIMEOUT_RESOURCE_SCORE = 'resource-score';

function costTotal(cost: Record<string, number> | undefined): number {
  return Object.values(cost ?? {}).reduce((sum, v) => sum + v, 0);
}

/** A player's timeout score and its parts (see `playerScore`). */
interface ScoreBreakdown {
  /** Stock held in their cities, times `match.timeout.stockWeight`. */
  stock: number;
  /**
   * Invested in standing buildings and their levels (a city's levels ×
   * `match.timeout.cityLevelWeight`); a site counts its cost × its progress.
   */
  buildings: number;
  /** Training cost of living military units, queued ones too (0 unless `timeout.army`). */
  army: number;
  total: number;
}

function scoreBreakdown(state: SimState, rules: RuleSet, playerId: PlayerId): ScoreBreakdown {
  const { stockWeight, cityLevelWeight } = rules.match.timeout;
  // Stock is kept in thousandths (fixed.ts); scores are in whole units.
  const stock = fromMilli(costTotal(playerStock(state, playerId))) * stockWeight;
  let buildings = 0;
  for (const b of Object.values(state.buildings)) {
    if (b.owner !== playerId) continue;
    // A site counts only as far as it's built (it has no levels yet), so
    // sites laid out in the last seconds don't inflate the score.
    const built = Math.min(1, Math.max(0, b.buildProgress));
    // City levels (buildings holding a territory) are the main sink: they
    // count at `cityLevelWeight` of their cost.
    const isCity = rules.buildings[b.type]?.territoryRadius !== undefined;
    const invested = buildingInvestment(
      rules,
      b,
      isCity ? cityLevelWeight : 1,
      upgradeFraction(rules, b)
    );
    buildings += costTotal(invested) * built;
  }
  let army = 0;
  if (rules.match.timeout.army) {
    for (const u of Object.values(state.units)) {
      const def = rules.units[u.type];
      if (u.owner === playerId && def?.military) army += costTotal(def.cost);
    }
    // Units in training are paid for: they count at what was paid.
    for (const t of queuedUnits(state, playerId)) {
      if (rules.units[t.unitType]?.military) army += fromMilli(costTotal(t.paid));
    }
  }
  return { stock, buildings, army, total: stock + buildings + army };
}

/**
 * Timeout score: the stock held in the player's cities (times
 * `match.timeout.stockWeight`, so hoarding scores less than spending) plus what they have
 * invested in buildings still standing — each building's cost (a city's is
 * the settler that founded it) and the cost of every level it has been raised
 * to (a city's levels × `match.timeout.cityLevelWeight`); a site counts its cost times its build progress, so one laid out just
 * before the end adds next to nothing — plus, with `match.timeout.army`, the training cost of their
 * living military units (stored and queued ones included; rank and hp don't count). A
 * lost city takes its stock with it.
 */
function playerScore(state: SimState, rules: RuleSet, playerId: PlayerId): number {
  return scoreBreakdown(state, rules, playerId).total;
}

function resolveByScore(state: SimState, rules: RuleSet, playerIds: readonly PlayerId[]): PlayerId {
  const sorted = [...playerIds].sort();
  let winner = sorted[0];
  let winnerScore = playerScore(state, rules, winner);
  for (let i = 1; i < sorted.length; i++) {
    const id = sorted[i];
    const score = playerScore(state, rules, id);
    if (score > winnerScore) {
      winnerScore = score;
      winner = id;
    }
  }
  return winner;
}

const victory: SystemFn = (state: SimState, ctx, inputs, dt): void => {
  if (state.status !== 'running') return;

  // Step 0: who can still hold or found a city. A player is out once they
  // have no city (including sites under construction) and no unit able to
  // found one; whatever else they own is removed with them.
  const alivePlayer: Record<PlayerId, boolean> = {};
  for (const unit of Object.values(state.units)) {
    if (ctx.rules.units[unit.type]?.actions.includes('found-city')) alivePlayer[unit.owner] = true;
  }
  for (const building of Object.values(state.buildings)) {
    if (isCity(building, ctx.rules)) alivePlayer[building.owner] = true;
  }

  // Step 1: elimination sweep
  for (const [playerId, player] of Object.entries(state.players)) {
    if (player.eliminated) continue;
    if (!alivePlayer[playerId]) eliminatePlayer(state, playerId);
  }

  // Step 2: partition players
  const playerIds = Object.keys(state.players);
  const alive = playerIds.filter((id) => !state.players[id].eliminated);
  const totalPlayers = playerIds.length;

  // Step 3: check for winner. Last player standing needs rivals; with none
  // left alive the match is over either way (a solo player who is out has
  // lost, rather than watching an empty map until the timeout).
  if (totalPlayers >= 2 && alive.length === 1) {
    state.status = 'ended';
    state.winner = alive[0];
    return;
  }
  if (alive.length === 0) {
    state.status = 'ended';
    if (totalPlayers >= 2) state.winner = resolveByScore(state, ctx.rules, playerIds);
    return;
  }

  // Step 4: the host ends the match (timed or not), scored as at the
  // timeout. Only the match host's `endMatch` counts (see `matchHost`).
  const host = matchHost(state);
  const hostEnds = inputs.some((i) => i.command.type === 'endMatch' && i.playerId === host);

  // Step 5: timeout (none with no time limit).
  // Victory runs before tick() advances elapsed, so compare the time this
  // tick ends at: the match ends on its last tick instead of playing one
  // extra. The epsilon absorbs float drift in the running sum of dt.
  const duration = matchDuration(state, ctx.rules);
  const timeUp = duration !== null && state.elapsed + dt >= duration - ELAPSED_EPSILON;
  if (!hostEnds && !timeUp) return;
  state.status = 'ended';
  if (hostEnds && !timeUp) state.endedBy = 'host';
  if (ctx.rules.match.timeout.type === TIMEOUT_RESOURCE_SCORE) {
    state.winner = resolveByScore(state, ctx.rules, alive);
  }
};

export type { ScoreBreakdown };
export { eliminatePlayer, playerScore, scoreBreakdown, victory };
